import { ResumeData, ResumeFormat } from "@/types/resume";
import { formatDateFull } from "@/lib/resumeUtils";
import { SectionVisibility, defaultSectionVisibility } from "@/components/SectionVisibilityControls";
import { SectionKey, DEFAULT_SECTION_ORDER } from "@/components/DraggableSectionTabs";

interface ResumePreviewHarvardProps {
  data: ResumeData;
  format?: ResumeFormat;
  sectionVisibility?: SectionVisibility;
  sectionOrder?: SectionKey[];
}

const SmallCapsHeader = ({ title }: { title: string }) => {
  const firstLetter = title.charAt(0).toUpperCase();
  const rest = title.slice(1).toUpperCase();
  return (
    <div style={{ marginBottom: '0' }}>
      <h2 style={{ margin: 0, paddingBottom: '2pt', borderBottom: '1px solid black', lineHeight: '1', letterSpacing: '0.05em' }}>
        <span style={{ fontSize: '14pt', fontWeight: 'bold' }}>{firstLetter}</span>
        <span style={{ fontSize: '11pt', fontWeight: 'bold' }}>{rest}</span>
      </h2>
    </div>
  );
};

const formatLinkDisplay = (url: string): string => {
  return url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
};

export const ResumePreviewHarvard = ({ data, format = 'standard', sectionVisibility = defaultSectionVisibility, sectionOrder = DEFAULT_SECTION_ORDER }: ResumePreviewHarvardProps) => {
  const hasContent = data.personalInfo.fullName || data.summary || (data.experience || []).length > 0;

  const experience = sectionVisibility.experience ? (data.experience || []) : [];
  const education = sectionVisibility.education ? (data.education || []) : [];
  const coreStrengths = sectionVisibility.skills ? (data.coreStrengths || []) : [];
  const customSections = sectionVisibility.customSections ? (data.customSections || []) : [];
  const skills = sectionVisibility.skills ? (data.skills || []) : [];
  const summary = sectionVisibility.summary ? data.summary : "";

  const isCompact = format === 'compact';

  if (!hasContent) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <div className="w-16 h-20 mx-auto mb-4 border-2 border-dashed border-muted-foreground/30 rounded-lg flex items-center justify-center">
            <span className="text-2xl text-muted-foreground/50">📄</span>
          </div>
          <p className="text-sm">Start filling in your details</p>
          <p className="text-xs mt-1">Your resume preview will appear here</p>
        </div>
      </div>
    );
  }

  const contactElements: React.ReactNode[] = [];
  if (data.personalInfo.location) contactElements.push(data.personalInfo.location);
  if (data.personalInfo.phone) contactElements.push(data.personalInfo.phone);
  if (data.personalInfo.email) {
    contactElements.push(
      <a key="email" href={`mailto:${data.personalInfo.email}`} style={{ color: '#0000EE', textDecoration: 'underline' }}>{data.personalInfo.email}</a>
    );
  }
  if (data.personalInfo.linkedin) {
    const url = data.personalInfo.linkedin.startsWith('http') ? data.personalInfo.linkedin : `https://${data.personalInfo.linkedin}`;
    contactElements.push(<a key="linkedin" href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#0000EE', textDecoration: 'underline' }}>LinkedIn</a>);
  }
  if (data.personalInfo.github) {
    const url = data.personalInfo.github.startsWith('http') ? data.personalInfo.github : `https://${data.personalInfo.github}`;
    contactElements.push(<a key="github" href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#0000EE', textDecoration: 'underline' }}>GitHub</a>);
  }
  if (data.personalInfo.portfolio) {
    const url = data.personalInfo.portfolio.startsWith('http') ? data.personalInfo.portfolio : `https://${data.personalInfo.portfolio}`;
    contactElements.push(<a key="portfolio" href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#0000EE', textDecoration: 'underline' }}>Portfolio</a>);
  }

  const sectionSpacingPt = isCompact ? 6 : 10;

  // Section renderers
  const renderEducation = () => {
    if (education.length === 0) return null;
    return (
      <section key="education" style={{ marginBottom: `${sectionSpacingPt}pt` }}>
        <SmallCapsHeader title="Education" />
        {education.map((edu) => (
          <div key={edu.id} style={{ marginTop: '4pt' }}>
            <div style={{ fontWeight: 'bold', fontSize: '10pt' }}>{edu.institution}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontStyle: 'italic' }}>{edu.degree}{edu.field && ` ${edu.field}`}{edu.gpa && `, GPA: ${edu.gpa}`}</span>
              {edu.location && <span style={{ fontStyle: 'italic' }}>{edu.location}</span>}
            </div>
          </div>
        ))}
      </section>
    );
  };

  const renderSummary = () => {
    if (!summary) return null;
    return (
      <section key="summary" style={{ marginBottom: `${sectionSpacingPt}pt` }}>
        <SmallCapsHeader title="Summary" />
        <p style={{ fontSize: '10pt', lineHeight: '12pt', textAlign: 'justify', margin: 0, marginTop: '3pt' }}>{summary}</p>
      </section>
    );
  };

  const renderExperience = () => {
    if (experience.length === 0) return null;
    return (
      <section key="experience" style={{ marginBottom: `${sectionSpacingPt}pt` }}>
        <SmallCapsHeader title="Experience" />
        {experience.map((exp) => {
          const startFormatted = formatDateFull(exp.startDate);
          const endFormatted = exp.current ? "Present" : formatDateFull(exp.endDate);
          const dateDisplay = (startFormatted || endFormatted)
            ? `${startFormatted || ""}${startFormatted && endFormatted ? " – " : ""}${endFormatted || ""}`
            : "";
          return (
            <div key={exp.id} style={{ marginTop: '6pt' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontWeight: 'bold', fontSize: '11pt' }}>{exp.jobTitle}</span>
                {dateDisplay && <span style={{ fontWeight: 'bold' }}>{dateDisplay}</span>}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontStyle: 'italic' }}>{exp.company}</span>
                {([exp.location, exp.workType].filter(Boolean).join(" | ")) && (
                  <span style={{ fontStyle: 'italic' }}>{[exp.location, exp.workType].filter(Boolean).join(" | ")}</span>
                )}
              </div>
              {(exp.bullets || []).filter(Boolean).length > 0 && (
                <ul style={{ listStyle: 'disc', margin: 0, paddingLeft: '18px', marginTop: '2pt', textAlign: 'justify' }}>
                  {(exp.bullets || []).filter(Boolean).map((bullet, idx) => (
                    <li key={idx} style={{ marginBottom: '1pt', textAlign: 'justify' }}>{bullet}</li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </section>
    );
  };

  const renderSkills = () => {
    if (coreStrengths.length > 0 && coreStrengths.some(c => c.category && c.skills)) {
      return (
        <section key="skills" style={{ marginBottom: `${sectionSpacingPt}pt` }}>
          <SmallCapsHeader title="Technical Skills" />
          <div style={{ marginTop: '4pt' }}>
            {coreStrengths.filter(c => c.category && c.skills).map((cat) => (
              <div key={cat.id} style={{ marginBottom: '2pt' }}>
                <span style={{ fontWeight: 'bold' }}>{cat.category}:</span>
                <span style={{ marginLeft: '4px' }}>{cat.skills}</span>
              </div>
            ))}
          </div>
        </section>
      );
    }
    if (skills.length > 0 && skills.some(Boolean)) {
      return (
        <section key="skills-legacy" style={{ marginBottom: `${sectionSpacingPt}pt` }}>
          <SmallCapsHeader title="Technical Skills" />
          <p style={{ marginTop: '4pt' }}>{skills.filter(Boolean).join(", ")}</p>
        </section>
      );
    }
    return null;
  };

  const renderCustomSections = () => {
    if (customSections.length === 0) return null;
    const projSections = customSections.filter(s => /project/i.test(s.title || ""));
    const certSections = customSections.filter(s => /certif|licen/i.test(s.title || ""));
    const otherSections = customSections.filter(s => s.title && !/certif|licen|project/i.test(s.title));

    return (
      <>
        {projSections.map((section) => (
          <section key={section.id} style={{ marginBottom: `${sectionSpacingPt}pt` }}>
            <SmallCapsHeader title={section.title} />
            {(section.items || []).map((item) => {
              if (item.type === 'bullet') {
                return (
                  <div key={item.id} style={{ marginTop: '2pt', fontSize: '10pt', lineHeight: '12pt', listStyleType: 'disc', paddingLeft: '14px', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0 }}>•</span>
                    {item.title}
                  </div>
                );
              }
              const projectUrl = item.link ? (item.link.startsWith('http') ? item.link : `https://${item.link}`) : null;
              return (
                <div key={item.id} style={{ marginTop: '4pt' }}>
                  <div>
                    <span style={{ fontWeight: 'bold' }}>{item.title}</span>
                    {item.subtitle && <span style={{ fontStyle: 'italic' }}> | {item.subtitle}</span>}
                    {projectUrl && (<><span> | </span><a href={projectUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#0000EE', textDecoration: 'underline' }}>Project Link</a></>)}
                  </div>
                  {(item.bullets || []).filter(Boolean).length > 0 && (
                    <ul style={{ listStyle: 'disc', margin: 0, paddingLeft: '18px', marginTop: '2pt', textAlign: 'justify' }}>
                      {(item.bullets || []).filter(Boolean).map((bullet, idx) => (
                        <li key={idx} style={{ marginBottom: '1pt', textAlign: 'justify' }}>{bullet}</li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </section>
        ))}
        {certSections.map((section) => (
          <section key={section.id} style={{ marginBottom: `${sectionSpacingPt}pt` }}>
            <SmallCapsHeader title={section.title} />
            {(section.items || []).map((item) => {
              if (item.type === 'bullet') {
                return (
                  <div key={item.id} style={{ marginTop: '2pt', fontSize: '10pt', lineHeight: '12pt', paddingLeft: '14px', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0 }}>•</span>
                    {item.title}
                  </div>
                );
              }
              const a = (item.title || "").toLowerCase().trim();
              const b = section.title.toLowerCase().trim();
              const showTitle = item.title && !(a === b || a.startsWith(b) || b.startsWith(a));
              return (
                <div key={item.id} style={{ marginTop: '2pt' }}>
                  {showTitle && <div style={{ fontWeight: 'normal' }}>{item.title}</div>}
                  {item.subtitle && <div style={{ fontStyle: 'italic', fontWeight: 'normal' }}>{item.subtitle}</div>}
                </div>
              );
            })}
          </section>
        ))}
        {otherSections.map((section) => (
          <section key={section.id} style={{ marginBottom: `${sectionSpacingPt}pt` }}>
            <SmallCapsHeader title={section.title} />
            {(section.items || []).map((item) => {
              if (item.type === 'bullet') {
                return (
                  <div key={item.id} style={{ marginTop: '2pt', fontSize: '10pt', lineHeight: '12pt', paddingLeft: '14px', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0 }}>•</span>
                    {item.title}
                  </div>
                );
              }
              const a = (item.title || "").toLowerCase().trim();
              const b = section.title.toLowerCase().trim();
              const showTitle = item.title && !(a === b || a.startsWith(b) || b.startsWith(a));
              return (
                <div key={item.id} style={{ marginTop: '2pt' }}>
                  {showTitle && <div style={{ fontWeight: 'normal' }}>{item.title}</div>}
                  {item.subtitle && <div style={{ fontStyle: 'italic', fontWeight: 'normal' }}>{item.subtitle}</div>}
                  {item.description && <div style={{ fontWeight: 'normal' }}>{item.description}</div>}
                  {(item.bullets || []).filter(Boolean).length > 0 && (
                    <ul style={{ listStyle: 'disc', margin: 0, paddingLeft: '18px', marginTop: '2pt', textAlign: 'justify', fontWeight: 'normal' }}>
                      {(item.bullets || []).filter(Boolean).map((bullet, idx) => (
                        <li key={idx} style={{ textAlign: 'justify' }}>{bullet}</li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </section>
        ))}
      </>
    );
  };

  const sectionRenderers: Record<string, () => React.ReactNode> = {
    education: renderEducation,
    summary: renderSummary,
    skills: renderSkills,
    experience: renderExperience,
    custom: renderCustomSections,
  };

  const orderedSections = sectionOrder
    .filter(key => key !== "personal")
    .map(key => sectionRenderers[key]?.())
    .filter(Boolean);

  return (
    <div 
      className="bg-white text-black shadow-lg"
      style={{
        width: '8.5in',
        minHeight: '11in',
        paddingTop: '0.4in',
        paddingBottom: '0.4in',
        paddingLeft: '0.55in',
        paddingRight: '0.55in',
        fontFamily: "'Times New Roman', Times, serif",
        fontSize: '11pt',
        lineHeight: '14pt',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ textAlign: 'center', marginBottom: `${sectionSpacingPt}pt` }}>
        <h1 style={{ fontSize: isCompact ? '18pt' : '21pt', fontWeight: 'bold', margin: 0, lineHeight: '1.3' }}>
          {data.personalInfo.fullName || "Your Name"}
        </h1>
        <div style={{ fontSize: '10pt', lineHeight: '14pt', marginTop: '4pt' }}>
          {contactElements.map((el, idx) => (
            <span key={idx}>{idx > 0 && ' | '}{el}</span>
          ))}
        </div>
      </div>

      {orderedSections}
    </div>
  );
};
